import { toChartNumber, type Money } from '@/lib/money'
import { shortSectorLabel } from '@/lib/sector-labels'
import type { CycleGrouping } from '@/schemas/signals'

/*
 * The cycle map's words, scale and boxes: what the grid and its skeleton
 * share, in a module with no components so the route's pending page may
 * import it (docs/route-loading.md, "Pitfalls").
 */

/*
 * The colour scale is the mock's continuous ramp (owner ruling S4): a cell's fill is the app's own
 * up or down fill (`--market-up-fill` / `--market-down-fill`, so the colour-blind palette carries
 * through) at an opacity that grows with the size of the move, `.12 + .054 × |v|`, capped at .92.
 * Zero is the neutral card-aware ground. The six legend swatches sit at the ramp's 2, 6 and 12.
 */
const RAMP_FLOOR = 0.12
const RAMP_STEP = 0.054
const RAMP_CAP = 0.92

/** The fill's opacity for a move of `size` percent (already rounded to the cell's one place). */
export const cycleRampOpacity = (size: number): number =>
  Math.min(RAMP_CAP, RAMP_FLOOR + RAMP_STEP * Math.abs(size))

/** Above this opacity the fill is the strong step's, so the text on it is the strong step's ink. */
const STRONG_FROM = 0.6

export interface CycleFill {
  /** The cell's background, a `color-mix` of the fill token over nothing. */
  background: string
  /** The Tailwind text class that clears 4.5:1 on that fill, from the heatmap's own pairs. */
  text: string
}

const mixOf = (token: string, opacity: number): string =>
  `color-mix(in srgb, var(${token}) ${(opacity * 100).toFixed(1)}%, transparent)`

/** A fill by direction (`1` up, `-1` down) at an opacity: the cells and the legend swatches. */
export const cycleFillOf = (direction: 1 | -1, opacity: number): CycleFill => ({
  background: mixOf(direction > 0 ? '--market-up-fill' : '--market-down-fill', opacity),
  text:
    opacity < STRONG_FROM
      ? 'text-foreground'
      : direction > 0
        ? 'text-market-heat-strong-up-foreground'
        : 'text-market-heat-strong-down-foreground',
})

/** The legend's six swatches, worst to best (mock: opacity .76 / .44 / .23 either side). */
export const CYCLE_LEGEND: readonly { direction: 1 | -1; size: number; label: string }[] = [
  { direction: -1, size: 12, label: '−12% or worse' },
  { direction: -1, size: 6, label: '−6%' },
  { direction: -1, size: 2, label: '−2%' },
  { direction: 1, size: 2, label: '+2%' },
  { direction: 1, size: 6, label: '+6%' },
  { direction: 1, size: 12, label: '+12% or better' },
]

const ONE_PLACE = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1, useGrouping: false })
const CELL_FIGURE = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

/** The value at the one place a cell prints, so a cell's colour is always the colour of its figure. */
const roundCell = (value: Money): number => Number(ONE_PLACE.format(toChartNumber(value)))

/** The cell's fill, or `null` for a figure that rounds to zero (the neutral ground) or will not read. */
export const cycleFillFor = (value: Money): CycleFill | null => {
  const rounded = roundCell(value)
  if (Number.isNaN(rounded) || rounded === 0) return null
  return cycleFillOf(rounded > 0 ? 1 : -1, cycleRampOpacity(rounded))
}

/**
 * A cell's figure: one place, signed, U+2212 for a fall, and no sign at all
 * once rounded to zero, so mm-be's `"-0.00"` (B11) prints "0.0". A cell's
 * screen-reader sentence carries the API's two places.
 */
export const formatCycleCell = (value: Money): string => {
  const rounded = roundCell(value)
  const body = CELL_FIGURE.format(Math.abs(rounded))
  return rounded > 0 ? `+${body}` : rounded < 0 ? `−${body}` : body
}

/**
 * Column heads for mm-be's month names, as the design shortens them. Explicit
 * rather than a cut at five letters (audit §2.5), so a spelling mm-be changes
 * prints whole instead of clipped. Anything not listed (the quarters' "Q1")
 * prints as sent.
 */
const PERIOD_SHORT: Readonly<Record<string, string>> = {
  Baisakh: 'Baisa',
  Jestha: 'Jesth',
  Ashar: 'Ashar',
  Shrawan: 'Shraw',
  Bhadra: 'Bhadr',
  Asoj: 'Asoj',
  Kartik: 'Karti',
  Mangsir: 'Mangs',
  Poush: 'Poush',
  Magh: 'Magh',
  Falgun: 'Falgu',
  Chait: 'Chait',
}

export const periodShortLabel = (period: string): string => PERIOD_SHORT[period] ?? period

/** The fiscal quarters mm-be compounds (Q1 = Shrawan to Asoj), for the popover's title. */
const QUARTER_MONTHS: Readonly<Record<string, string>> = {
  Q1: 'Shrawan to Asoj',
  Q2: 'Kartik to Poush',
  Q3: 'Magh to Chait',
  Q4: 'Baisakh to Ashar',
}

/** "Jestha", or "Q1 (Shrawan to Asoj)". */
export const periodFullLabel = (period: string): string => {
  const months = QUARTER_MONTHS[period]
  return months === undefined ? period : `${period} (${months})`
}

const NUMBER_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
] as const

/** "five years", "one year": how many BS years mm-be actually averaged, which can be fewer than asked. */
export const yearsPhrase = (count: number): string =>
  `${NUMBER_WORDS[count] ?? String(count)} ${count === 1 ? 'year' : 'years'}`

/**
 * The sector's row label, as the mock words it ("Banking", "Dev bank", "Non-life ins"). Only the
 * names mm-be sends for the sectors the mock shortens are listed; any other falls back to the
 * app's `shortSectorLabel`, then to the name itself.
 */
const CYCLE_SECTOR_LABEL: Readonly<Record<string, string>> = {
  'Commercial Banks': 'Banking',
  'Development Banks': 'Dev bank',
  'Hotels and Tourism': 'Hotels',
  'Life Insurance': 'Life insurance',
  'Manufacturing and Processing': 'Manufacture',
  'Non-Life Insurance': 'Non-life ins',
  Tradings: 'Trading',
}

export const cycleSectorLabel = (name: string): string =>
  CYCLE_SECTOR_LABEL[name] ?? shortSectorLabel(name)

/** The read-line at rest, before any cell is pointed at (mock copy). */
export const CYCLE_READ_REST = 'Point at a cell to read it; click to keep it.'

/**
 * The read-line for one cell (mock copy): "Hydropower in Shrawan: +6.7% on average. Best month
 * Poush (+23.7%), weakest Bhadra (−10.6%)." Read off the sector's own row, so it needs nothing the
 * grid does not hold. A sector with one period of data has no best or weakest to name, and a cell
 * with no data says so.
 */
export const cycleReadLine = ({
  sector,
  periods,
  changes,
  column,
  grouping,
}: {
  sector: string
  periods: readonly string[]
  changes: readonly (Money | null)[]
  column: number
  grouping: CycleGrouping
}): string => {
  const period = periods[column] ?? ''
  const here = changes[column] ?? null
  const head = `${sector} in ${period}`
  if (here === null) return `${head}: no data in these years.`
  const figure = (value: Money): string => `${formatCycleCell(value)}%`
  const known = periods.flatMap((name, index) => {
    const value = changes[index] ?? null
    return value === null ? [] : [{ name, value }]
  })
  const lead = `${head}: ${figure(here)} on average.`
  if (known.length < 2) return lead
  const ranked = [...known].sort((a, b) => toChartNumber(b.value) - toChartNumber(a.value))
  const best = ranked[0]
  const worst = ranked[ranked.length - 1]
  if (best === undefined || worst === undefined) return lead
  const unit = grouping === 'quarter' ? 'quarter' : 'month'
  return `${lead} Best ${unit} ${best.name} (${figure(best.value)}), weakest ${worst.name} (${figure(worst.value)}).`
}

/*
 * The grid's boxes, shared with its skeleton. The sector column is a fixed 112px (the mock's) and the
 * period columns share the rest equally; the grid always spans the page column it sits in, with no
 * card around it (the mock draws none). Each cell is 32px tall (the mock's 30, on the 4-grid) and
 * never narrower than 56px (the mock's 57), which holds a head's label, its sort arrow and the
 * 14px figure; below that the table keeps its minimum width and scrolls sideways with the sector
 * column pinned, so a figure never narrows into an ellipsis.
 */
export const CYCLE_ROW_HEIGHT = 'h-8'
export const CYCLE_SECTOR_COLUMN = 'w-28'
export const CYCLE_SECTOR_WIDTH = 'w-full'

/**
 * The table's floor for `columns` periods: the 112px sector column, 56px a cell, and the 4px
 * gutters (one more than the columns, plus the outer pair). As a style because the period count is
 * data.
 */
export const cycleGridMinWidth = (columns: number): string =>
  `calc(7rem + ${String(columns)} * 3.5rem + ${String(columns + 2)} * 0.25rem)`

/**
 * `border-separate` + `border-spacing-1` is the 4px gutter between cells.
 * `table-fixed` + `w-full` hands the sector column its 112px (`<colgroup>`)
 * and splits the rest evenly across the periods. `relative` keeps every `sr-only` span inside the
 * scroller rather than stretching the page (the reason Broker by sector gives).
 */
export const CYCLE_GRID_TABLE = 'relative w-full table-fixed border-separate border-spacing-1'

/**
 * The pinned sector column: the page's own ground under it, and a 4px ring in the same ground over
 * the gutters, so cells scrolled past slide beneath it. `left-1` is where it already sits (the
 * table's 4px outer spacing).
 */
export const CYCLE_STICKY_CELL = 'sticky left-1 z-1 bg-background p-0 ring-4 ring-background'

/** `ScrollArea`'s wrapper and viewport, for the skeleton to copy without importing it. */
export const CYCLE_SCROLL_WRAPPER = '-m-1'
export const CYCLE_SKELETON_VIEWPORT = 'scrollbar-slim min-w-0 grow overflow-x-auto'
